import * as React from 'react';

// API
import apibridge from '../../apibridge';

//UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import SingleSelect from '../../components/SingleSelect';
import DateInput from '../../components/DateInput';
import DateDayExpiryInput from '../../components/DateDayExpiryInput';
import FileUpload from '../../components/FileUpload';
import LoadingSpinner from '../../components/LoadingSpinner';
import FilesList from '../../components/FilesList';

//TYPES
import { TrainingAdditionalTrainingEditCommand } from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface AddAdditionalTrainingModalTypes {
	trainingId?: string;
	closeModal?: closeModalType;
	getUserTrainingData?: () => {};
}

const AddAdditionalTrainingModal: React.FC<AddAdditionalTrainingModalTypes> = ({
	trainingId,
	closeModal,
	getUserTrainingData
}) => {
	//  Get all Additional Training Types from Redux store
	const additionalTrainingTypes = useAppSelector((state) => state.lists.additionalTrainingTypes);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(!!trainingId);

	// Set up Data state and initial Data
	const initialAddAdditionalTraining: TrainingAdditionalTrainingEditCommand = {
		id: undefined,
		type: undefined,
		other: undefined,
		dateOfTraining: undefined,
		expiry: undefined,
		description: undefined,
		fileName: undefined,
		displayFileName: undefined,
		attachmentUrl: undefined
	};

	const [addAdditionalTraining, setAddAdditionalTraining] = React.useState(initialAddAdditionalTraining);

	const getAdditionalTrainingDetail = async () => {
		if (trainingId) {
			setIsLoading(true);

			const response = await apibridge.getAdditionalTrainingDetail(trainingId);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setAddAdditionalTraining(response.result);

				setIsLoading(false);
			}
		} else {
			setAddAdditionalTraining(initialAddAdditionalTraining);

			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getAdditionalTrainingDetail();
	}, []);

	const submitHandler = async (values: any) => {
		setIsLoading(true);

		const data: TrainingAdditionalTrainingEditCommand = {
			id: addAdditionalTraining.id,
			type: values.typeInput,
			other: values.otherInput,
			dateOfTraining: values.dateOfTrainingInput,
			expiry: values.expiryInput,
			description: values.descriptionInput,
			fileName: values.fileName,
			displayFileName: values.displayFileName,
			attachmentUrl: values.attachmentUrl
		};

		const response = await apibridge.postTrainingAdditionalTrainingEdit(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}

			if (getUserTrainingData) {
				getUserTrainingData();
			}
		}
	};

	const validationSchema = Yup.object({
		typeInput: Yup.string().required('Required'),
		otherInput: Yup.string().when('typeInput', {
			is: 'Other',
			then: Yup.string().required('Required')
		}),
		dateOfTrainingInput: Yup.string().required('Required'),
		descriptionInput: Yup.string().required('Required')
	});

	const initialValues = {
		typeInput: addAdditionalTraining.type || '',
		otherInput: addAdditionalTraining.other || '',
		dateOfTrainingInput: addAdditionalTraining.dateOfTraining || '',
		expiryInput: addAdditionalTraining.expiry || '',
		descriptionInput: addAdditionalTraining.description || '',
		fileName: addAdditionalTraining.fileName,
		displayFileName: addAdditionalTraining.displayFileName,
		attachmentUrl: addAdditionalTraining.attachmentUrl
	};

	return (
		<>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<div className="modal-min-height-lg">
								<LoadingSpinner show={isLoading} />
							</div>
						) : (
							<Formik
								initialValues={initialValues}
								validationSchema={validationSchema}
								onSubmit={submitHandler}
								enableReinitialize
							>
								{(formik) => {
									return (
										<Form translate="yes" className="general-form mtop-0">
											<div className="form-group">
												<label htmlFor="typeInput" className="label">
													Training type
												</label>

												<SingleSelect
													options={additionalTrainingTypes}
													name="typeInput"
													value={formik.values.typeInput}
													onChange={(val: string) => formik.setFieldValue('typeInput', val)}
												/>

												<ErrorMessage name="typeInput" component="div" className="error-message" />
											</div>

											{formik.values.typeInput === 'Other' && (
												<div className="form-group">
													<label htmlFor="otherInput" className="label">
														Title of Training
													</label>

													<div className="form-field">
														<Field name="otherInput" id="otherInput" type="text" className="form-input" />

														<ErrorMessage name="otherInput" component="div" className="error-message" />
													</div>
												</div>
											)}

											<div className="form-group row">
												<div className="col-6">
													<label htmlFor="dateOfTrainingInput" className="label">
														Date of training
													</label>

													<DateInput
														dateTimeString={formik.values.dateOfTrainingInput || ''}
														onChange={(val: string) => formik.setFieldValue('dateOfTrainingInput', val)}
														excludeFuture={true}
													/>

													<ErrorMessage name="dateOfTrainingInput" component="div" className="error-message" />
												</div>

												<div className="col-6">
													<label htmlFor="expiryInput" className="label">
														Expiry date <span className="sub-grey-text bold">(if applicable)</span>
													</label>

													<DateDayExpiryInput
														dateTimeString={formik.values.expiryInput || ''}
														onChange={(val: string) => formik.setFieldValue('expiryInput', val)}
														excludePast={true}
													/>
												</div>
											</div>

											<div className="form-group">
												<label htmlFor="descriptionInput" className="label">
													Description of training completed
												</label>

												<div className="form-field">
													<Field
														as="textarea"
														name="descriptionInput"
														id="descriptionInput"
														type="text"
														className="form-input"
													/>

													<ErrorMessage name="descriptionInput" component="div" className="error-message" />
												</div>
											</div>

											<div className="form-group">
												<label htmlFor="Attachments" className="label">
													Training document <span className="sub-grey-text bold">(optional)</span>
												</label>

												<div className="form-field mbtom-14">
													<FilesList
														onChange={() => {
															formik.setFieldValue('displayFileName', '');
														}}
														multiple={false}
														files={
															!formik.values.displayFileName
																? []
																: [
																		{
																			id: formik.values.displayFileName,
																			displayFileName: formik.values.displayFileName,
																			link: formik.values.attachmentUrl
																		}
																  ]
														}
													/>

													<FileUpload
														name="Attachments"
														maxSizeMb={5}
														multiple={false}
														onChange={(val: string, files: any) => {
															formik.setFieldValue('fileName', val);
														}}
													/>
												</div>
											</div>

											<div className="form-button-group">
												<Button type="submit" className="btn-primary w-100">
													Save changes
												</Button>
											</div>
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</>
	);
};

export default AddAdditionalTrainingModal;
